/**
 * Workspace — Page publique mobile-first pour la collecte documentaire CONFILEX.
 *
 * URL : /d/:accessToken
 *
 * - Aucune connexion requise (ACL via le token opaque)
 * - Drag & drop, photo, capture, PDF, note vocale
 * - Liste chronologique des pièces déjà transmises
 * - Suppression d'une pièce possible
 *
 * Design : mobile-first, ton conciergerie premium CONFILEX.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Upload, FileText, Image as ImageIcon, Mic, Trash2, CheckCircle2,
  AlertTriangle, Camera, FileUp, Loader2, RefreshCw, FileDown, Package,
  Copy as CopyIcon,
} from "lucide-react";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuditPanel from "@/components/AuditPanel";
import AIDisclaimer from "@/components/AIDisclaimer";
import MaturityBadge from "@/components/MaturityBadge";
import MessagingChannels from "@/components/MessagingChannels";
import { useAmbientDucking, useAmbientScene } from "@/lib/useAmbientDucking";

const MAX_SIZE = 25 * 1024 * 1024;

const ACCEPT = [
  "application/pdf", "image/jpeg", "image/png", "image/webp", "image/heic", "image/heif",
  "text/plain", "text/csv",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "audio/mp4", "audio/m4a", "audio/mpeg", "audio/wav", "audio/webm",
].join(",");

function iconFor(mime) {
  if (!mime) return FileText;
  if (mime.startsWith("image/")) return ImageIcon;
  if (mime.startsWith("audio/")) return Mic;
  return FileText;
}

function formatSize(n) {
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
}

const CATEGORY_COLORS = {
  Contrats:     "bg-amber-50 text-amber-800 border-amber-200",
  Échanges:     "bg-emerald-50 text-emerald-800 border-emerald-200",
  Factures:     "bg-amber-50 text-amber-800 border-amber-200",
  Courriers:    "bg-violet-50 text-violet-700 border-violet-200",
  Procédures:   "bg-rose-50 text-rose-700 border-rose-200",
  Comptabilité: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Bancaire:     "bg-teal-50 text-teal-700 border-teal-200",
  RH:           "bg-pink-50 text-pink-700 border-pink-200",
  Fiscal:       "bg-orange-50 text-orange-700 border-orange-200",
  Immobilier:   "bg-lime-50 text-lime-700 border-lime-200",
  Autres:       "bg-slate-50 text-slate-600 border-slate-200",
};

function CategoryBadge({ category }) {
  if (!category) {
    return (
      <span className="text-[10px] px-2 py-0.5 rounded-full border border-dashed border-slate-300 text-slate-400 italic">
        en analyse…
      </span>
    );
  }
  const cls = CATEGORY_COLORS[category] || CATEGORY_COLORS.Autres;
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${cls}`}>
      {category}
    </span>
  );
}

function formatDocDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function Workspace() {
  const { accessToken } = useParams();
  const { user } = useAuth();
  // Garde-fou métier 89€ : aucun affichage d'audit payant pour les spécialistes/admins
  const hideAuditCTA = user && ["expert", "admin"].includes(user.role);
  const [workspace, setWorkspace] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Ducking : abaisse l'ambiance musicale pendant un upload ou un drag actif.
  // Scene : déclare la phase courante pour l'orchestration UX globale.
  const uploadDuck = useAmbientDucking("workspace-upload");
  useAmbientScene(uploading ? "upload" : (dragging ? "upload" : "welcome"));
  useEffect(() => {
    if (uploading || dragging) uploadDuck.engage(0.3);
    else uploadDuck.release();
  }, [uploading, dragging, uploadDuck]);

  const load = useCallback(async () => {
    try {
      const r = await apiClient.get(`/workspace/${accessToken}`);
      setWorkspace(r.data.workspace);
      setDocuments(r.data.documents || []);
      setTimeline(r.data.timeline || []);
      setLoading(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "Dossier introuvable ou expiré.");
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh tant que des pièces sont en cours de classification
  useEffect(() => {
    const hasPending = (documents || []).some(
      (d) => d.classification_status === "pending"
    );
    if (!hasPending) return undefined;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [documents, load]);

  const uploadFiles = async (files) => {
    setError(null);
    for (const file of files) {
      if (file.size > MAX_SIZE) {
        setError(`Fichier trop volumineux : ${file.name} (max 25 Mo).`);
        continue;
      }
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        await apiClient.post(`/workspace/${accessToken}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (e) {
        setError(e?.response?.data?.detail || `Échec de l'envoi : ${file.name}`);
      }
      setUploading(false);
    }
    await load();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) uploadFiles(files);
  };

  const onFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) uploadFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  const removeDoc = async (id) => {
    if (!window.confirm("Supprimer cette pièce du dossier ?")) return;
    try {
      await apiClient.delete(`/workspace/${accessToken}/doc/${id}`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Suppression impossible.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F1] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#C9A961] animate-spin" />
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="min-h-screen bg-[#FAF7F1] flex items-center justify-center p-4">
        <div className="max-w-md text-center bg-white rounded-2xl p-6 shadow-md">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
          <h1 className="font-serif italic text-xl text-[#0B3A2A] mb-2">Dossier introuvable</h1>
          <p className="text-sm text-slate-600">{error || "Le lien n'est plus valide."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F1]" data-testid="workspace-page">
      {/* Header institutionnel */}
      <header className="bg-gradient-to-br from-[#0B3A2A] to-[#0B3A2A] text-white">
        <div className="max-w-2xl mx-auto px-5 py-7">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9A961] mb-2">CONFILEX</p>
          <h1 className="font-serif italic text-2xl leading-tight">
            {workspace.first_name ? `Bonjour ${workspace.first_name},` : "Bonjour,"}
          </h1>
          <p className="text-sm text-white/80 mt-2 leading-relaxed">
            Votre dossier est ouvert. Ajoutez vos documents quand vous voulez —
            photo, PDF, capture, note vocale… à votre rythme.
          </p>
          <div className="flex items-center gap-3 mt-4 text-xs text-white/60">
            <span className="px-2 py-1 rounded-full bg-white/10 border border-white/15">
              {workspace.doc_count || 0} pièce{(workspace.doc_count || 0) > 1 ? "s" : ""}
            </span>
            <span className="text-[#C9A961]/80 italic">{workspace.phone_masked}</span>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-6 space-y-5">
        {/* Aperçu gratuit du score de maturité */}
        <MaturityBadge accessToken={accessToken} refreshKey={documents.length} />

        {/* CTA Audit CONFILEX — masqué pour spécialistes/admins */}
        {!hideAuditCTA && (
          <AuditPanel accessToken={accessToken} workspace={workspace} />
        )}

        {/* Zone d'upload — premier élément */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`rounded-2xl border-2 border-dashed transition-all p-6 text-center ${
            dragging
              ? "border-[#C9A961] bg-[#C9A961]/10"
              : "border-[#0B3A2A]/15 bg-white hover:border-[#C9A961]/50"
          }`}
          data-testid="workspace-dropzone"
        >
          <Upload className="w-7 h-7 text-[#C9A961] mx-auto mb-2" />
          <p className="text-sm text-[#0B3A2A] font-medium">
            Glissez vos pièces ici, ou utilisez les boutons ci-dessous
          </p>
          <p className="text-[11px] text-slate-500 italic mt-1">
            PDF, photo, image, document, note vocale — jusqu'à 25 Mo
          </p>

          <div className="grid grid-cols-2 gap-2 mt-4">
            {/* Caméra (mobile-first) */}
            <button
              type="button"
              data-testid="workspace-take-photo"
              onClick={() => cameraInputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#0B3A2A] text-white text-sm hover:brightness-110 disabled:opacity-50 transition"
            >
              <Camera className="w-4 h-4 text-[#C9A961]" />
              Photo
            </button>
            {/* Browse */}
            <button
              type="button"
              data-testid="workspace-browse"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white border border-[#0B3A2A]/20 text-[#0B3A2A] text-sm hover:bg-[#C9A961]/10 hover:border-[#C9A961] disabled:opacity-50 transition"
            >
              <FileUp className="w-4 h-4 text-[#C9A961]" />
              Mes fichiers
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            multiple
            className="hidden"
            onChange={onFileChange}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={onFileChange}
          />

          {uploading && (
            <div className="mt-4 flex items-center justify-center gap-2 text-xs text-[#C9A961]">
              <Loader2 className="w-3 h-3 animate-spin" /> Transmission en cours…
            </div>
          )}
          {error && (
            <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg flex items-start gap-1.5 text-left">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {error}
            </p>
          )}
        </div>

        {/* Liste des pièces */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Mes pièces</h2>
            <div className="flex items-center gap-2">
              {documents.length > 0 && (
                <>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/workspace/${accessToken}/bordereau.pdf`}
                    target="_blank" rel="noreferrer"
                    data-testid="workspace-bordereau-btn"
                    className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-[#0B3A2A]/15 text-[#0B3A2A] hover:border-[#C9A961] hover:text-[#8A6E3A] transition"
                  >
                    <FileDown className="w-3 h-3" /> Bordereau
                  </a>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/workspace/${accessToken}/export.zip`}
                    data-testid="workspace-export-zip-btn"
                    className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#0B3A2A] text-white hover:brightness-110 transition"
                  >
                    <Package className="w-3 h-3 text-[#C9A961]" /> Exporter
                  </a>
                </>
              )}
              <button
                type="button"
                onClick={load}
                className="text-[11px] text-[#C9A961] inline-flex items-center gap-1 hover:underline"
              >
                <RefreshCw className="w-3 h-3" /> Actualiser
              </button>
            </div>
          </div>

          {documents.length === 0 ? (
            <div className="text-center text-sm text-slate-500 italic py-6">
              Aucune pièce pour le moment.<br />
              Vous pouvez en ajouter à tout moment.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="workspace-docs-list">
              {documents.map((d) => {
                const Icon = iconFor(d.mime);
                const isPending = d.classification_status === "pending";
                const displayLabel = d.auto_label || d.filename_original;
                const docDate = formatDocDate(d.doc_date);
                return (
                  <li
                    key={d.id}
                    className="flex items-start gap-3 bg-white rounded-xl px-3 py-3 shadow-sm border border-slate-100"
                    data-testid="workspace-doc-row"
                  >
                    <div className="w-9 h-9 rounded-lg bg-[#C9A961]/15 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-[#8A6E3A]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm text-[#0B3A2A] font-medium truncate">
                          Pièce {d.piece_index} — {displayLabel}
                        </p>
                        <CategoryBadge category={d.category} />
                        {d.duplicate_of && (
                          <span
                            className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-1"
                            title="Document identique déjà transmis"
                          >
                            <CopyIcon className="w-2.5 h-2.5" /> similaire détecté
                          </span>
                        )}
                        {isPending && (
                          <Loader2 className="w-3 h-3 text-slate-300 animate-spin" />
                        )}
                      </div>
                      {d.auto_summary && (
                        <p className="text-[11px] text-slate-600 mt-1 line-clamp-2">
                          {d.auto_summary}
                        </p>
                      )}
                      <p className="text-[11px] text-slate-500 mt-1">
                        {docDate && <span className="text-[#8A6E3A]">{docDate}</span>}
                        {docDate && " · "}
                        {formatSize(d.size)} · reçue le{" "}
                        {new Date(d.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/workspace/${accessToken}/doc/${d.id}`}
                        target="_blank" rel="noreferrer"
                        className="text-[11px] text-[#0B3A2A] hover:text-[#C9A961] px-2 py-1"
                      >
                        Voir
                      </a>
                      <button
                        type="button"
                        onClick={() => removeDoc(d.id)}
                        className="p-1.5 text-slate-400 hover:text-red-500 transition"
                        aria-label="Supprimer"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Chronologie compacte */}
        {timeline.length > 0 && (
          <section>
            <h2 className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-2">Chronologie</h2>
            <ul className="space-y-1.5 text-xs text-slate-600">
              {timeline.slice(-8).reverse().map((t) => (
                <li key={t.id} className="flex items-start gap-2">
                  <CheckCircle2 className="w-3 h-3 text-[#C9A961] mt-0.5 shrink-0" />
                  <span>
                    <span className="text-slate-400 mr-1.5">
                      {new Date(t.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}
                    </span>
                    {t.summary}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <footer className="pt-6 pb-12">
          <AIDisclaimer variant="footer" statements={["documentaire", "verify", "no_advice", "expert_master"]} />
          <p className="text-[10px] text-slate-400 italic text-center mt-4 leading-relaxed">
            Service institutionnel d'intermédiation. CONFILEX assure une organisation
            documentaire automatisée — structuration, classement intelligent, chronologie
            automatisée — sans délivrer de conseil juridique, fiscal, comptable ou notarial.
          </p>
        </footer>
      </main>
      <div className="max-w-2xl mx-auto px-5 pb-12 -mt-6">
        <MessagingChannels
          context="workspace"
          workspaceToken={accessToken}
          variant="row"
          title="Poursuivre la conversation"
          subtitle="Vous pouvez continuer à transmettre des informations sur votre canal préféré — les échanges sont rattachés à votre dossier."
          testId="workspace-messaging-channels"
        />
      </div>
    </div>
  );
}
